import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Info,
  Check,
  Settings,
  CheckCircle,
  XCircle,
  IdCard
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import TopNav from "../components/TopNav";
import LessonLayout from "../components/LessonLayout";
import LessonIcon from "../components/LessonIcon";
import { useProgress } from "../contexts/ProgressContext";
import { useGetModulesQuery } from "../redux/Clinictrainingapi";
import sectionIconMap from "../utils/sectionIconMap";

/* ═══════════════════════════════════════════════════════════════
   CONTENT TYPE INFERENCE MAP
   When the API returns content: null, we infer the correct
   content type from the section heading (case-insensitive).
   This is more reliable than order-based matching because
   API section order values may differ from original lesson numbers.
═══════════════════════════════════════════════════════════════ */
const HEADING_TO_CONTENT_TYPE = {
  // Module 1
  "what is hipaa?":                     "centered-definition",
  "what is phi?":                       "phi-definition",
  "who must follow hipaa?":             "combined-lessons",
  "real-life examples":                 "real-life-examples",
  "your personal responsibility":       "responsibility",
  // Module 2
  "when can you share phi?":            "numbered-cards",
  "the minimum necessary rule":         "highlight",
  "patient rights":                     "rights",
  "common privacy mistakes":            "mistakes",
  "verification & social media integrity": "verification-social-media",
  "handling verbal requests":           "scenario-analysis",
  // Module 3
  "three types of safeguards":          "three-safeguards",
  "role-based access":                  "role-based-access",
  "physical safeguards":                "physical-safeguards",
  "technical safeguards":               "technical-safeguards",
  "securing your workflow":             "two-column-topics",
  "remote work safety":                 "remote-work-checklist",
  "real-life scenario":                 "real-life-scenario",
  // Module 4
  "what is a hipaa breach?":            "breach-definition",
  "what you must do immediately":       "breach-immediate-actions",
  "standard operating procedures for incident response": "breach-sop",
  "not every incident is automatically a breach": "breach-incident-rule",
  "handling breach incidents":          "breach-scenario",
  // Module 5
  "ba and baa defined":                 "ba-definition",
  "shared responsibility":              "shared-responsibility",
  "proper use of the platform":         "platform-usage",
  // Module 6
  "staff reminders":                    "staff-reminders",
  "billing and admin staff reminders":  "billing-reminders",
  "the 5-second compliance rule":       "five-second-rule",
};

/**
 * Resolve the effective content type for a section.
 * Uses the API value if present, otherwise infers from heading.
 */
function resolveContentType(section) {
  if (section.content) return section.content;
  const heading = (section.heading || "").toLowerCase().trim();
  return HEADING_TO_CONTENT_TYPE[heading] || null;
}

/* ═══════════════════════════════════════════════════════════════
   COMBINED LESSON HELPERS
═══════════════════════════════════════════════════════════════ */
function isCombinedLesson(lesson) {
  return resolveContentType(lesson) === "combined-lessons";
}

function getLessonBreadcrumb(moduleOrder, sections, currentSection) {
  let visualNum = 0;
  for (const s of sections) {
    visualNum += 1;
    if (s.id === currentSection.id) {
      if (isCombinedLesson(s)) {
        return `LESSON ${moduleOrder}.${visualNum} - ${moduleOrder}.${visualNum + 1}`;
      }
      return `LESSON ${moduleOrder}.${visualNum}`;
    }
    if (isCombinedLesson(s)) visualNum += 1;
  }
  return `LESSON ${moduleOrder}.${currentSection.order}`;
}

/* ═══════════════════════════════════════════════════════════════
   CONTENT BUILDER
═══════════════════════════════════════════════════════════════ */
function buildContent(section, contentType) {
  const listItems = section.list_items
    ? section.list_items.split("\n").filter(Boolean)
    : [];

  const base = {
    subtitle: section.subtitle,
    heading: section.heading,
    body: section.body,
    definition: section.definition,
    quote: section.quote,
    callout: section.callout,
  };

  switch (contentType) {
    case "centered-definition":
      return {
        ...base,
        listItems: listItems.map((text) => ({ icon: sectionIconMap[text] ?? "CheckCircle", text })),
        bottomCards: [
          { icon: "Hospital", title: "Provider COMPLIANCE", description: "Every provider must comply." },
          { icon: "BadgeCheck", title: "STAFF RESPONSIBILITY", description: "Every staff member must comply." },
        ],
      };

    case "phi-definition":
      return {
        ...base,
        criteria: [
          { number: 1, text: "Identifies a patient", badge: "AND" },
          { number: 2, text: "Relates to health, treatment, or payment." },
        ],
        examplesTitle: "EXAMPLES OF PHI",
        examples: listItems.map((text) => ({ icon: sectionIconMap[text] ?? "FileText", text })),
        quote: section.quote ? {
          text: '"If you can identify a patient and connect them to medical information —',
          highlight: "it is PHI.",
          after: '"',
        } : null,
      };

    case "combined-lessons":
      return {
        sections: [
          {
            subtitle: section.subtitle,
            heading: section.heading,
            body: section.body,
            gridItems: [
              { icon: "Stethoscope", text: "Doctors" },
              { icon: "HeartPulse", text: "Nurses" },
              { icon: "UserRound", text: "Front desk staff" },
              { icon: "Receipt", text: "Billing teams" },
              { icon: "Briefcase", text: "Managers" },
              { icon: "Monitor", text: "IT staff" },
              { icon: "Link", text: "Vendors handling PHI" },
            ],
            quote: section.quote,
          },
          {
            subtitle: "LESSON 1.5",
            heading: "Why HIPAA Matters",
            body: "Violations can result in:",
            violationItems: section.extra?.violationItems ?? [],
            callout: section.extra?.callout ?? null,
          },
        ],
      };

    case "real-life-examples":
      return { ...base, scenarios: section.extra?.scenarios ?? [], callout: section.extra?.callout ?? null };

    case "responsibility":
      return {
        ...base,
        cards: [
          { icon: "ShieldCheck", title: "Information Security", description: "Protecting patient information" },
          { icon: "Users", title: "Verbal Privacy", description: "Speaking carefully" },
          { icon: "KeyRound", title: "Access Control", description: "Securing access to systems" },
          { icon: "Bell", title: "Instant Reporting", description: "Reporting mistakes immediately" },
        ],
        callout: section.quote ? {
          icon: "Award",
          text: "\"HIPAA compliance is not just management's job.",
          highlight: "It is everyone's job.",
          after: '"',
        } : null,
      };

    case "numbered-cards": {
      // Support API-driven items via section.extra.items, falling back to defaults.
      // Each item's `examples` field accepts a string, an array of strings, or
      // a newline-delimited string — all normalised to string[].
      const normaliseExamples = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.filter(Boolean);
        // single string — may contain newlines as delimiter
        return raw.split("\n").map((s) => s.trim()).filter(Boolean);
      };

      const defaultItems = [
        {
          number: 1,
          icon: "Stethoscope",
          title: "Treatment",
          description: "Sharing information to treat a patient.",
          examples: [
            "A doctor discusses lab results with a nurse.",
            "A clinic sends records to a specialist for care.",
          ],
        },
        {
          number: 2,
          icon: "CreditCard",
          title: "Payment",
          description: "Sharing information to get paid.",
          examples: [
            "Sending claims to insurance.",
            "Verifying coverage details.",
          ],
        },
        {
          number: 3,
          icon: "Building",
          title: "Healthcare Operations",
          description: "Administrative activities needed to run the provider.",
          examples: [
            "Quality improvement reviews",
            "Internal audits",
            "Staff training using limited case information"],
        },
      ];

      const apiItems = section.extra?.items;
      const items = apiItems
        ? apiItems.map((it, i) => ({
            number: it.number ?? i + 1,
            icon: it.icon ?? defaultItems[i]?.icon ?? "FileText",
            title: it.title ?? "",
            description: it.description ?? "",
            examples: normaliseExamples(it.examples ?? it.example),
          }))
        : defaultItems;

      return {
        ...base,
        items,
        note: section.callout ? { title: "Important Note", text: section.callout } : null,
      };
    }

    case "highlight":
      return {
        ...base,
        importantText: "This is very important. When using or sharing PHI, you must",
        highlightedText: "only share the minimum necessary information.",
        pills: ["NOT THE ENTIRE FILE", "NOT EXTRA DETAILS"],
        afterText: "Only what is required for the task.",
        example: section.callout ? { type: "practical", text: section.callout } : null,
        callout: section.quote ? { type: "selfcheck", text: `"${section.quote}"` } : null,
      };

    case "rights":
      return {
        ...base,
        sections: [
          {
            number: 4,
            title: "When You MUST Get Patient Permission",
            body: "Covered entities must obtain a written authorization before using or sharing PHI for the following purposes:",
            bullets: ["Sharing with employers", "Marketing initiatives", "Patient stories or photos", "Media interactions", "Non-healthcare purposes"],
          },
          {
            number: 5,
            title: "Patient Rights Under HIPAA",
            body: "Patients have specific rights regarding their own health information that healthcare providers must honor:",
            list: listItems.map((text) => ({ icon: sectionIconMap[text] ?? "CheckCircle", text })),
          },
        ],
      };

    case "mistakes": {
      const mistakeIcons = ["Volume2", "Eye", "Monitor", "MessageSquare", "KeyRound"];
      return {
        ...base,
        mistakes: listItems.map((text, i) => {
          const [title, description] = text.split(" — ");
          return { icon: mistakeIcons[i] ?? "AlertTriangle", title: title || text, description: description || "" };
        }),
        warning: "All of these are violations.",
      };
    }

    case "verification-social-media":
      return {
        ...base,
        sections: [
          {
            number: 7,
            title: "Phone and Identity Verification",
            body: "Before sharing information over the phone:",
            checklist: listItems.map((text) => ({ icon: "CheckCircle", text })),
            warning: "Never assume the caller is who they claim to be",
          },
          {
            number: 8,
            title: "Social Media Warning",
            label: "STRICT PROHIBITION: NEVER",
            prohibitions: [
              { icon: "ImageOff", text: "Post patient photos without written authorization" },
              { icon: "EyeOff", text: 'Share "interesting cases" that identify patients' },
              { icon: "MessageSquareOff", text: "Discuss patient situations online" },
            ],
            warning: section.callout ?? "Even indirect clues can identify someone.",
          },
        ],
      };

    case "scenario-analysis":
      return {
        ...base,
        sectionLabel: "SECTION 9",
        challenge: {
          label: "CHALLENGE",
          scenario: section.extra?.challenge?.scenario ?? section.body,
          inboundInquiry: section.extra?.challenge ? {
            label: section.extra.challenge.inboundLabel,
            text: section.extra.challenge.inboundText,
          } : null,
        },
        protocol: {
          label: "PROTOCOL",
          heading: section.extra?.protocol?.heading ?? "Correct Action",
          steps: (section.extra?.protocol?.steps ?? []).map((step) => ({
            ...step,
            icon: step.title?.includes("CONSTRAINT") ? "Ban" : "CheckCircle",
          })),
        },
        complianceNote: section.extra?.complianceNote ?? null,
      };

    case "three-safeguards":
      return {
        ...base,
        items: listItems.map((title, i) => ({ number: i + 1, title })),
        followOn: {
          subtitle: "TOPIC 3",
          heading: "Administrative Safeguards",
          definition: section.definition,
          examplesLabel: "EXAMPLES:",
          examples: [
            { icon: "GraduationCap", label: "Staff training" },
            { icon: "KeyRound", label: "Access controls" },
            { icon: "ClipboardList", label: "Risk assessments" },
            { icon: "ShieldAlert", label: "Incident response plans" },
            { icon: "Briefcase", label: "Assigned compliance responsibility" },
          ],
        },
      };

    case "role-based-access": {
      const roles = ["BILLING STAFF", "FRONT DESK STAFF", "IT STAFF"];
      const roleIcons = ["CreditCard", "UserRound", "Monitor"];
      return {
        ...base,
        subheading: section.body,
        examples: listItems.map((text, i) => ({ icon: roleIcons[i] ?? "Building", role: roles[i] ?? "", text })),
        callouts: [
          { type: "info", icon: "UserCheck", text: "Access should match job duties." },
          { type: "warning", icon: "ShieldAlert", text: "Never try to bypass system limits." },
        ],
      };
    }

    case "physical-safeguards": {
      const physIcons = ["Lock", "DoorClosed", "Server", "EyeOff", "Minus"];
      return {
        ...base,
        subheading: section.body,
        examplesLabel: "EXAMPLES OF SAFEGUARDS",
        examples: listItems.map((text, i) => ({ icon: physIcons[i] ?? "Shield", text })),
        callout: section.callout ? {
          label: "SIMPLE RULE",
          text: "If a patient or visitor can see your screen,",
          highlight: "that is a problem.",
          footer: "Always lock your screen when stepping away.",
          icon: "Lock",
        } : null,
      };
    }

    case "technical-safeguards":
      return {
        ...base,
        badge: "HIPAA COMPLIANCE STANDARD",
        items: [
          { icon: "KeyRound", title: "Strong Passwords", description: listItems[0] ?? "", highlight: "Never share your password." },
          { icon: "Smartphone", title: "Two-Factor Authentication (2FA)", description: listItems[1] ?? "" },
          { icon: "LogOut", title: "Logging Out", description: listItems[2] ?? "" },
        ],
      };

    case "two-column-topics":
      return {
        ...base,
        columns: (section.extra?.columns ?? []).map((col) => ({
          ...col,
          icon: col.topicLabel?.includes("07") ? "AtSign" : "Smartphone",
        })),
      };

    case "remote-work-checklist":
      return {
        topic9: {
          subtitle: section.subtitle,
          heading: section.heading,
          summary: section.extra?.summary ?? section.body,
          cards: section.extra?.cards ?? [],
        },
        topic10: {
          subtitle: "TOPIC 10",
          heading: "Quick Security Checklist",
          intro: section.extra?.checklistIntro ?? "Before accessing patient information, ask yourself:",
          checklistItems: listItems.length > 0 ? listItems : [
            "Am I authorized to see this?",
            "Do I need all this information?",
            "Is my device secure?",
            "Is my screen private?",
            "Will I log out when finished?",
          ],
          callout: section.extra?.callout ?? "If you cannot answer yes to these — stop.",
        },
      };

    case "real-life-scenario":
      return {
        ...base,
        incident: { icon: "Activity", title: "The Incident", text: `"${section.body}"` },
        assessment: { icon: "ShieldAlert", title: "The Assessment", verdict: "That is a violation. Even if it was accidental.", verdictColor: "danger", text: section.callout ?? "" },
      };

    case "breach-definition":
      return {
        definition: {
          sectionLabel: section.subtitle,
          heading: section.heading,
          body: section.body,
          listLabel: section.extra?.listLabel ?? "A BREACH HAPPENS WHEN PHI IS:",
          listItems: section.extra?.listItems ?? listItems,
          footer: section.extra?.footer ?? section.callout,
        },
        examples: {
          sectionLabel: section.extra?.examplesSectionLabel ?? "4.3 REAL-WORLD SCENARIOS",
          heading: section.extra?.examplesHeading ?? "Common Examples of Breaches",
          items: section.extra?.examples ?? [],
          note: section.extra?.examplesNote ?? null,
        },
      };

    case "breach-immediate-actions":
      return {
        sectionLabel: section.subtitle,
        heading: section.heading,
        body: section.body,
        steps: listItems,
        callout: section.callout ? { label: "TIME MATTERS", icon: "Info", text: section.callout } : null,
      };

    case "breach-sop":
      return {
        subtitle: section.subtitle,
        heading: section.heading,
        whySpeed: {
          number: 5,
          title: "Why Speed Matters",
          intro: "Under federal rules, providers may be required to:",
          items: ["Investigate the incident", "Notify affected patients", "Notify the government"],
          footer: "Delays can increase penalties. Quick reporting reduces damage and legal risk.",
        },
        whatNotToDo: {
          number: 6,
          title: "What NOT To Do",
          items: listItems.map((i) => i.replace("Do NOT ", "")),
          footer: section.callout ?? "Transparency protects everyone.",
        },
      };

    case "breach-incident-rule": {
      const sentences = section.body.split(/(?<=\.) /);
      return {
        incident: { number: 7, heading: section.heading, body1: sentences.slice(0, 2).join(" "), body2: sentences.slice(2).join(" ") },
        rule: { number: 9, label: "REMEMBER THIS RULE", quote: section.quote ? `"${section.quote}"` : "", sublabel: "COMPLIANCE STANDARD", icon: "Scale" },
      };
    }

    case "breach-scenario":
      return {
        subtitle: section.subtitle,
        heading: section.heading ?? "Real-Life Scenario",
        situation: { icon: "Info", label: "THE SITUATION", text: `"${section.body}"` },
        correctAction: { icon: "CheckCircle", label: "CORRECT ACTION", steps: listItems.map((text, i) => ({ text, bold: i < 3 })) },
        rule: { icon: "GitFork", label: "REMEMBER THIS RULE", text: section.quote ?? "", sublabel: "KEY TAKEAWAY FOR MODULE 4", sublabelIcon: "Info" },
      };

    case "ba-definition":
      return {
        subtitle: section.subtitle,
        heading: section.heading,
        baSection: {
          number: "01",
          title: "What Is a Business Associate (BA)?",
          body: section.body,
          examples: (section.extra?.examples ?? []).map((ex) => ({ ...ex, icon: sectionIconMap[ex.title] ?? "Building" })),
          footer: section.extra?.footer ?? section.callout ?? "",
        },
        baaSection: {
          number: "02",
          title: "What Is a Business Associate Agreement (BAA)?",
          definition: "A BAA is a legally required contract that must be in place before any PHI is shared.",
          diagram: section.extra?.diagram ?? { left: "THE PROVIDER", center: "BAA REQUIRED", right: "NEXALIFE" },
          body: section.extra?.baaBody ?? "",
          example: section.extra?.baaExample ?? "",
        },
      };

    case "shared-responsibility":
      return {
        subtitle: section.subtitle ?? "MODULE 5: SECURITY PARTNERSHIPS",
        heading: "4. SHARED RESPONSIBILITY — NOT TRANSFERRED RESPONSIBILITY",
        body: section.body,
        columns: section.extra?.columns ?? [],
        footer: section.extra?.footer ?? section.callout ?? "",
      };

    case "platform-usage":
      return {
        sections: [
          {
            subtitle: "SECTION 5.5",
            heading: "PROPER USE OF THE PLATFORM",
            intro: "Using NexaLife correctly is part of compliance. Staff must:",
            items: [
              { icon: "UserCheck", title: "Assigned Logins Only", description: "Each user must use their own unique credentials assigned by the administrator." },
              { icon: "Ban", title: "No Account Sharing", description: "Sharing passwords or login sessions is a direct violation of security protocols." },
              { icon: "FileText", title: "Enter Accurate Information", description: "All data entered into the system must be accurate and complete." },
              { icon: "Minus", title: "Avoid Unnecessary Data Entry", description: "Only enter data that is necessary and relevant to patient care." },
              { icon: "Bell", title: "Report System Issues Immediately", description: "Any technical issues or security concerns must be reported to IT support promptly." },
            ],
          },
          {
            subtitle: "SECTION 5.6",
            heading: "WHY THIS MATTERS BEFORE ACTIVATION",
            intro: "Before a provider is activated:",
            checklist: section.extra?.checklist ?? [],
            footer: section.callout ?? "Compliance is strongest when both sides understand their role.",
            quote: section.extra?.quote ?? { label: "SIMPLE REMINDER", text: section.quote ?? "" },
          },
        ],
      };

    case "staff-reminders":
      return {
        subtitle: section.subtitle,
        heading: section.heading,
        intro: section.body,
        sections: (section.extra?.sections ?? []).map((s) => ({
          number: s.number,
          title: s.title,
          columns: [
            { type: "always", icon: "CheckCircle", title: "ALWAYS", items: s.always },
            { type: "never", icon: "XCircle", title: "NEVER", items: s.never },
          ],
        })),
      };

    case "billing-reminders":
      return {
        sections: [
          {
            subtitle: "SECTION 6.4",
            heading: "BILLING AND ADMIN STAFF REMINDERS",
            intro: section.body,
            items: listItems,
            tip: { icon: "AlertTriangle", text: section.extra?.tip ?? section.callout ?? "" },
          },
          {
            subtitle: "SECTION 6.5",
            heading: "DEVICE AND WORKSPACE SAFETY",
            groups: (section.extra?.groups ?? []).map((g) => ({
              icon: g.title.toLowerCase().includes("desk") ? "Lock" : "Smartphone",
              title: g.title,
              items: g.items,
            })),
          },
        ],
      };

    case "five-second-rule":
      return {
        subtitle: section.subtitle ?? "MODULE 6: BEST PRACTICES",
        heading: section.heading,
        card: { icon: "Clock", intro: section.body, steps: listItems, warning: section.callout ?? "If unsure — stop and ask." },
      };

    default:
      return base;
  }
}

/* ═══════════════════════════════════════════════════════════════
   SHARED RESPONSIVE WRAPPERS
═══════════════════════════════════════════════════════════════ */

// All lesson cards get responsive padding
const Card = ({ children, className = "" }) => (
  <div className={`bg-white p-5 sm:p-8 rounded-2xl ${className}`}>{children}</div>
);

const SectionLabel = ({ children }) => (
  <div className="text-xs font-bold text-primary tracking-widest uppercase mb-3">{children}</div>
);

const Heading = ({ children, className = "" }) => (
  <h2 className={`text-xl sm:text-2xl font-bold text-gray-900 ${className}`}>{children}</h2>
);

/* ═══════════════════════════════════════════════════════════════
   MODULES 1 & 2 LESSON TYPES
═══════════════════════════════════════════════════════════════ */

function CenteredDefinition({ content }) {
  return (
    <Card className="text-center">
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-6">{content.heading}</Heading>
      {content.definition && <p className="text-base font-semibold text-gray-800 mb-3">{content.definition}</p>}
      <p className="text-gray-500 mb-8 text-sm">{content.body}</p>
      <div className="space-y-3 mb-8 text-left max-w-md mx-auto">
        {(content.listItems ?? []).map((item, i) => (
          <motion.div key={i} className="flex items-center gap-3 bg-[#F8FAFC] border border-[#F1F5F9CC] rounded-xl px-4 sm:px-5 py-3.5"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.06 }}>
            <LessonIcon name={item.icon} size={18} className="text-primary shrink-0" />
            <span className="text-sm text-gray-700">{item.text}</span>
          </motion.div>
        ))}
      </div>
      {content.heading === "What Is HIPAA?" && (
        <div className="grid grid-cols-2 gap-3 mt-8">
          {content.bottomCards.map((card, i) => (
            <motion.div key={i} className="bg-[#01ABCA0D] border border-[#01ABCA1A] rounded-xl p-4 sm:p-5 text-center"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}>
              <LessonIcon name={card.icon} size={22} className="text-primary mx-auto mb-2" />
              <div className="text-xs font-bold text-gray-800 tracking-wider uppercase mb-1">{card.title}</div>
              <p className="text-xs text-gray-500">{card.description}</p>
            </motion.div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PhiDefinition({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-6">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 text-sm sm:text-base">{content.body}</p>
      <div className="space-y-3 mb-8">
        {(content.criteria ?? []).map((c, i) => (
          <motion.div key={i} className="flex items-center gap-3 sm:gap-4 bg-[#F8FAFC] border border-[#F1F5F9] rounded-xl px-4 sm:px-5 py-3.5 sm:py-4"
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.1 }}>
            <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">{c.number}</span>
            <span className="text-sm sm:text-base font-semibold text-gray-800">{c.text}</span>
            {c.badge && <span className="text-xs font-bold text-primary ml-auto">{c.badge}</span>}
          </motion.div>
        ))}
      </div>
      <SectionLabel>{content.examplesTitle}</SectionLabel>
      {/* Single col on mobile, 2 cols on sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {(content.examples ?? []).map((ex, i) => (
          <motion.div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3.5 border border-gray-100"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.3 + i * 0.06 }}>
            <LessonIcon name={ex.icon} size={16} className="text-primary/60 shrink-0" />
            <span className="text-sm text-gray-700">{ex.text}</span>
          </motion.div>
        ))}
      </div>
      {content.quote && (
        <motion.div className="border-l-4 border-primary bg-cyan-50/50 rounded-r-xl p-4 sm:p-5"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
            {content.quote.text} <span className="text-primary font-semibold">{content.quote.highlight}</span>{content.quote.after}
          </p>
        </motion.div>
      )}
    </Card>
  );
}

function CombinedLessons({ content }) {
  return (
    <Card className="space-y-12 sm:space-y-16">
      {(content.sections ?? []).map((section, si) => (
        <motion.div key={si} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: si * 0.2 }}>
          <SectionLabel>{section.subtitle}</SectionLabel>
          <Heading className="mb-4">{section.heading}</Heading>
          <p className="text-gray-800 font-semibold mb-5 text-sm sm:text-base">{section.body}</p>
          {section.gridItems && (
            <div className="bg-gray-50 rounded-xl p-4 sm:p-5 mb-6">
              {/* Single col on mobile, 2 cols on sm+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {section.gridItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-[#F8FAFC] border border-[#F1F5F9] rounded-lg px-4 py-3">
                    <LessonIcon name={item.icon} size={16} className="text-primary/50 shrink-0" />
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {section.quote && (
            <div className="border-l-4 border-primary bg-gray-50 rounded-r-xl p-4 sm:p-5">
              <p className="text-gray-700 font-semibold text-sm">{section.quote}</p>
            </div>
          )}
          {section.violationItems && (
            <div className="space-y-4 mb-6">
              {section.violationItems.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-400 shrink-0 mt-2" />
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">{item.title}</div>
                    <p className="text-xs text-gray-500">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {section.callout && (
            <motion.div className="bg-gray-800 rounded-xl p-5 sm:p-6 relative overflow-hidden"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
              <SectionLabel>{section.callout.label}</SectionLabel>
              <p className="text-gray-300 text-sm leading-relaxed">
                {section.callout.text} <span className="text-primary font-semibold">{section.callout.highlight}</span>
              </p>
            </motion.div>
          )}
        </motion.div>
      ))}
    </Card>
  );
}

function RealLifeExamples({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-6 sm:mb-8">{content.heading}</Heading>
      <div className="space-y-6 mb-8">
        {(content.scenarios ?? []).map((scenario, i) => (
          <motion.div key={i} className="relative bg-white rounded-xl border border-gray-200 p-5 sm:p-6 pt-8"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + i * 0.15 }}>
            <div className="absolute -top-4 left-5 w-9 h-9 rounded-lg flex items-center justify-center bg-red-100">
              <LessonIcon name="AlertTriangle" size={16} className="text-red-500" />
            </div>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">{scenario.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{scenario.description}</p>
              </div>
              {scenario.badge && (
                <span className="text-[10px] font-bold text-red-500 border border-red-200 bg-red-50 rounded-full px-2 sm:px-3 py-1 tracking-wider uppercase shrink-0">
                  {scenario.badge}
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      {content.callout && (
        <motion.div className="bg-gray-800 rounded-xl p-5 sm:p-6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs font-bold text-primary tracking-widest uppercase">{content.callout.label}</span>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            {content.callout.text} <span className="text-primary font-semibold">{content.callout.highlight}</span>{content.callout.after}
          </p>
        </motion.div>
      )}
    </Card>
  );
}

function ResponsibilityLesson({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-4">{content.heading}</Heading>
      <p className="text-gray-500 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      {/* 1 col mobile, 2 col sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 sm:mb-8">
        {(content.cards ?? []).map((card, i) => (
          <motion.div key={i} className="bg-gray-50 rounded-xl p-4 flex items-start gap-3 border border-gray-100"
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.08 }}>
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <LessonIcon name={card.icon} size={16} className="text-primary" />
            </div>
            <div>
              <div className="font-semibold text-gray-800 text-sm mb-0.5">{card.title}</div>
              <p className="text-xs text-gray-500">{card.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
      {content.callout && (
        <motion.div className="bg-gray-800 rounded-xl p-5 sm:p-6 text-center"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <LessonIcon name={content.callout.icon} size={24} className="text-primary mx-auto mb-3" />
          <p className="text-gray-300 text-sm leading-relaxed">
            {content.callout.text}<br />
            <span className="text-primary font-semibold">{content.callout.highlight}</span>{content.callout.after}
          </p>
        </motion.div>
      )}
    </Card>
  );
}

function HighlightLesson({ content }) {
  return (
    <Card className="text-center">
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-6 sm:mb-8">{content.heading}</Heading>
      <p className="text-base sm:text-lg font-semibold text-gray-800 mb-1 max-w-lg mx-auto leading-relaxed">
        {content.importantText}{" "}
        <span className="text-primary underline decoration-primary/30 underline-offset-4">{content.highlightedText}</span>
      </p>
      {content.pills && (
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 my-5 sm:my-6">
          {content.pills.map((pill, i) => (
            <span key={i} className="text-xs font-bold tracking-wider text-[#DC2626] border border-[#FEE2E2] bg-[#FEF2F2] rounded-full px-3 sm:px-4 py-1.5 uppercase">{pill}</span>
          ))}
        </div>
      )}
      <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">{content.afterText}</p>
      {content.example && (
        <div className="text-left border-l-4 border-primary bg-cyan-50/50 rounded-r-xl p-4 sm:p-5 mb-6">
          <div className="text-xs font-bold text-primary tracking-widest uppercase mb-2">Practical Example</div>
          <p className="text-sm text-gray-600">{content.example.text}</p>
        </div>
      )}
      {content.callout && (
        <motion.div className="bg-gray-900 rounded-xl p-5 sm:p-6 text-center"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3 }}>
          <div className="text-xs font-bold text-primary tracking-widest uppercase mb-2">Internal Self-Check</div>
          <p className="text-white text-sm font-medium italic leading-relaxed">{content.callout.text}</p>
        </motion.div>
      )}
    </Card>
  );
}

function NumberedCards({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-3">{content.heading}</Heading>
      <p className="text-gray-500 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      <div className="space-y-4 mb-8">
        {(content.items ?? []).map((item, i) => {
          // Normalise: support legacy `example` string OR new `examples` array
          const examples = Array.isArray(item.examples)
            ? item.examples
            : item.example
              ? [item.example]
              : [];

          return (
            <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + i * 0.1 }}>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-9 h-9 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                  <LessonIcon name={item.icon} size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">
                    {item.number}. {item.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">{item.description}</p>

                  {examples.length > 0 && (
                    <div className="space-y-1.5">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {examples.length === 1 ? "Example" : "Examples"}
                      </span>
                      <div className="flex flex-col gap-1.5">
                        {examples.map((ex, ei) => (
                          <div
                            key={ei}
                            className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
                          >
                            {/* bullet dot */}
                            <div className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0 mt-1.5" />
                            <span className="text-xs text-gray-600 leading-snug">{ex}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      {content.note && <NoteBlock note={content.note} />}
    </Card>
  );
}

function RightsLesson({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-6 sm:mb-8">{content.heading}</Heading>
      {(content.sections ?? []).map((section, si) => (
        <motion.div key={si} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 mb-5"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + si * 0.1 }}>
          <div className="flex items-start gap-3 mb-4">
            <span className="w-7 h-7 rounded-lg bg-primary/5 flex items-center justify-center text-primary font-bold text-xs shrink-0">{section.number}</span>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">{section.title}</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4 leading-relaxed">{section.body}</p>
          {section.bullets && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {section.bullets.map((b, bi) => (
                <div key={bi} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-sm text-gray-700">{b}</span>
                </div>
              ))}
            </div>
          )}
          {section.list && (
            <div className="space-y-2">
              {section.list.map((item, li) => (
                <div key={li} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                  <LessonIcon name={item.icon} size={16} className="text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      ))}
    </Card>
  );
}

function MistakesLesson({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <div className="w-12 h-1 bg-primary rounded-full mb-6" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {(content.mistakes ?? []).map((m, i) => (
          <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3"
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.06 }}>
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
              <LessonIcon name={m.icon} size={16} className="text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm mb-0.5">{m.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{m.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
      {content.warning && (
        <motion.div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
          <AlertTriangle size={18} className="text-yellow-500 shrink-0" />
          <p className="text-sm text-yellow-800 font-medium">{content.warning}</p>
        </motion.div>
      )}
    </Card>
  );
}

function VerificationSocialMedia({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <p className="text-gray-500 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      <div className="space-y-6 sm:space-y-8">
        {(content.sections ?? []).map((section, si) => (
          <motion.div key={si} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + si * 0.15 }}>
            <div className="flex items-start gap-3 mb-4">
              <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">{section.number}</span>
              <h3 className="text-base sm:text-lg font-bold text-gray-900">{section.title}</h3>
            </div>
            {section.body && <p className="text-sm text-gray-500 mb-4">{section.body}</p>}
            {section.label && <div className="text-xs font-bold text-red-500 tracking-widest uppercase mb-3">{section.label}</div>}
            {section.checklist && (
              <div className="space-y-2.5 mb-4">
                {section.checklist.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <LessonIcon name={item.icon} size={16} className="text-primary shrink-0" />
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            )}
            {section.prohibitions && (
              <div className="space-y-2.5 mb-4">
                {section.prohibitions.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                    <LessonIcon name={item.icon} size={16} className="text-red-400 shrink-0" />
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            )}
            {section.warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
                <Info size={14} className="text-amber-500 shrink-0" />
                <span className="text-sm text-amber-700">{section.warning}</span>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

function ScenarioAnalysis({ content }) {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-bold text-primary bg-primary/5 border border-primary/20 rounded-md px-2.5 py-1 tracking-wide">{content.subtitle}</span>
        <span className="text-xs text-gray-400 tracking-wide">{content.sectionLabel}</span>
      </div>
      <Heading className="mb-6 sm:mb-8">{content.heading}</Heading>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <motion.div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
          initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <div className="flex items-center gap-2 mb-5">
            <LessonIcon name="IdCard" size={16} className="text-primary shrink-0" />
            <span className="text-xs font-bold text-gray-700 tracking-widest uppercase">The Scenario</span>
          </div>
          <p className="text-gray-700 text-sm leading-relaxed">"{content.challenge?.scenario}"</p>
          {content.challenge?.inboundInquiry?.text && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <LessonIcon name="Phone" size={14} className="text-primary" />
                <span className="text-xs font-bold text-primary tracking-widest uppercase">{content.challenge.inboundInquiry.label}</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">{content.challenge.inboundInquiry.text}</p>
            </div>
          )}
        </motion.div>
        <motion.div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
          initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
          <div className="flex items-center gap-2 mb-5">
            <LessonIcon name="CheckCircle" size={16} className="text-primary shrink-0" />
            <span className="text-xs font-bold text-gray-700 tracking-widest uppercase">{content.protocol?.label}</span>
          </div>
          <div className="space-y-2.5">
            {(content.protocol?.steps ?? []).map((step, i) => (
              <motion.div key={i} className="flex items-center gap-3 bg-[#ECFDF5] border border-[#D1FAE5] rounded-xl px-4 py-3"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.3 + i * 0.08 }}>
                <LessonIcon name={step.title?.toLowerCase().includes("do not") || step.title?.toLowerCase().includes("without") ? "Ban" : step.title?.toLowerCase().includes("verify") ? "UserCheck" : "FileText"} size={15} className="text-[#047857] shrink-0" />
                <span className="text-sm font-medium text-[#047857]">{step.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      {content.complianceNote && (
        <motion.div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-start gap-4"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <span className="text-amber-600 font-bold text-base">!</span>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-sm mb-1">{content.complianceNote.title}</h4>
            <p className="text-sm text-gray-500 leading-relaxed">{content.complianceNote.text}</p>
          </div>
        </motion.div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MODULE 3 LESSON TYPES
═══════════════════════════════════════════════════════════════ */

function ThreeSafeguards({ content }) {
  return (
    <Card className="space-y-10 sm:space-y-12">
      <div>
        <SectionLabel>{content.subtitle}</SectionLabel>
        <Heading className="mb-3">{content.heading}</Heading>
        <p className="text-gray-500 mb-6 text-sm sm:text-base">{content.body}</p>
        <div className="space-y-3">
          {(content.items ?? []).map((item, i) => (
            <motion.div key={i} className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 px-4 sm:px-5 py-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.08 }}>
              <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">{item.number}</span>
              <span className="font-semibold text-gray-800 text-sm sm:text-base">{item.title}</span>
            </motion.div>
          ))}
        </div>
      </div>
      {content.followOn && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}>
          <SectionLabel>{content.followOn.subtitle}</SectionLabel>
          <Heading className="mb-4">{content.followOn.heading}</Heading>
          <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 sm:px-5 py-4 mb-6">
            <p className="text-gray-700 font-medium text-sm sm:text-base">{content.followOn.definition}</p>
          </div>
          <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-3">{content.followOn.examplesLabel}</div>
          <div className="flex flex-wrap gap-2">
            {(content.followOn.examples ?? []).map((ex, i) => (
              <motion.span key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 sm:px-4 py-2 text-sm text-gray-700"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2, delay: 0.5 + i * 0.06 }}>
                <LessonIcon name={ex.icon} size={14} className="text-primary/60" />
                {ex.label}
              </motion.span>
            ))}
          </div>
        </motion.div>
      )}
    </Card>
  );
}

function RoleBasedAccess({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">{content.subheading}</p>
      <div className="space-y-3 mb-8">
        {(content.examples ?? []).map((ex, i) => (
          <motion.div key={i} className="bg-white rounded-xl border border-gray-200 px-4 sm:px-5 py-4 flex items-start sm:items-center gap-3 sm:gap-4"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.1 }}>
            <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              <LessonIcon name={ex.icon} size={16} className="text-gray-400" />
            </div>
            <div>
              <div className="text-xs font-bold text-gray-500 tracking-widest uppercase mb-0.5">EXAMPLE: {ex.role}</div>
              <p className="text-sm text-gray-700">{ex.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="space-y-3">
        {(content.callouts ?? []).map((callout, i) => (
          <motion.div key={i} className={`flex items-center gap-3 rounded-xl px-4 sm:px-5 py-4 ${callout.type === "warning" ? "bg-red-50 border border-red-100" : "bg-cyan-50/60 border border-cyan-100"}`}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.4 + i * 0.1 }}>
            <LessonIcon name={callout.icon} size={18} className={callout.type === "warning" ? "text-red-400 shrink-0" : "text-primary shrink-0"} />
            <span className={`text-sm font-medium ${callout.type === "warning" ? "text-red-700" : "text-gray-700"}`}>{callout.text}</span>
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

function PhysicalSafeguards({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 text-sm sm:text-base">{content.subheading}</p>
      <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-4">{content.examplesLabel}</div>
      {/* Single col mobile, 2 col sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {(content.examples ?? []).map((ex, i) => (
          <motion.div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3.5"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.07 }}>
            <LessonIcon name={ex.icon} size={16} className="text-primary/60 shrink-0" />
            <span className="text-sm text-gray-700">{ex.text}</span>
          </motion.div>
        ))}
      </div>
      {content.callout && (
        <motion.div className="bg-gray-900 rounded-xl p-5 sm:p-6 relative overflow-hidden"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
          <div className="text-xs font-bold text-primary tracking-widest uppercase mb-3">{content.callout.label}</div>
          <p className="text-gray-200 text-sm leading-relaxed mb-3">
            {content.callout.text} <span className="text-primary font-semibold">{content.callout.highlight}</span>
          </p>
          <div className="flex items-center gap-2 text-gray-400 text-xs">
            <LessonIcon name={content.callout.icon} size={13} className="text-gray-500 shrink-0" />
            {content.callout.footer}
          </div>
        </motion.div>
      )}
    </Card>
  );
}

function TechnicalSafeguards({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-4">{content.heading}</Heading>
      {content.badge && (
        <span className="inline-block text-xs font-bold text-primary border border-primary/30 bg-primary/5 rounded-full px-4 py-1.5 tracking-widest uppercase mb-6 sm:mb-8">{content.badge}</span>
      )}
      <div className="space-y-4">
        {(content.items ?? []).map((item, i) => (
          <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-start gap-3 sm:gap-4"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + i * 0.1 }}>
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 border border-primary/10">
              <LessonIcon name={item.icon} size={18} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-1.5 text-sm sm:text-base">{item.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                {item.description}
                {item.highlight && <> <span className="text-primary font-semibold">{item.highlight}</span></>}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

function TwoColumnTopics({ content }) {
  const iconStyle = (type) => {
    if (type === "warning") return "text-red-400";
    if (type === "cross") return "text-gray-400";
    return "text-primary";
  };
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {(content.columns ?? []).map((col, ci) => (
          <motion.div key={ci} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + ci * 0.15 }}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <LessonIcon name={col.icon} size={18} className="text-primary" />
              </div>
              <span className="text-xs font-bold text-gray-400 tracking-widest">{col.topicLabel}</span>
            </div>
            <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">{col.title}</h3>
            {col.intro && <p className="text-sm text-gray-500 mb-4">{col.intro}</p>}
            <div className="space-y-2.5 mb-4">
              {(col.items ?? []).map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <LessonIcon name={item.type === "check" ? "CheckCircle" : item.type === "warning" ? "AlertTriangle" : "XCircle"} size={15} className={`${iconStyle(item.type)} shrink-0 mt-0.5`} />
                  <span className={`text-sm leading-snug ${item.bold ? "font-semibold text-gray-800" : "text-gray-600"}`}>{item.text}</span>
                </div>
              ))}
            </div>
            {col.footer && <p className="text-xs text-gray-400 italic border-t border-gray-100 pt-3 mt-3">{col.footer}</p>}
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

function RemoteWorkChecklist({ content }) {
  const [checked, setChecked] = useState({});
  const { topic9, topic10 } = content;
  const allChecked = topic10?.checklistItems?.length > 0 && Object.keys(checked).length === topic10.checklistItems.length && Object.values(checked).every(Boolean);
  const toggle = (i) => setChecked((prev) => ({ ...prev, [i]: !prev[i] }));

  return (
    <Card className="space-y-10 sm:space-y-12">
      <div>
        <SectionLabel>{topic9?.subtitle}</SectionLabel>
        <Heading className="mb-4">{topic9?.heading}</Heading>
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 sm:px-5 py-4 mb-6">
          <p className="text-sm text-gray-600">{topic9?.summary}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          {(topic9?.cards ?? []).slice(0, 3).map((card, i) => (
            <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.08 }}>
              <h4 className="font-semibold text-gray-800 text-sm mb-1">{card.title}</h4>
              <p className="text-xs text-gray-500 leading-relaxed">{card.description}</p>
            </motion.div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(topic9?.cards ?? []).slice(3).map((card, i) => (
            <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.34 + i * 0.08 }}>
              <h4 className="font-semibold text-gray-800 text-sm mb-1">{card.title}</h4>
              <p className="text-xs text-gray-500 leading-relaxed">{card.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.5 }}>
        <SectionLabel>{topic10?.subtitle}</SectionLabel>
        <Heading className="mb-2">{topic10?.heading}</Heading>
        <p className="text-gray-500 mb-6 text-sm sm:text-base">{topic10?.intro}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {(topic10?.checklistItems ?? []).map((item, i) => (
            <button key={i} onClick={() => toggle(i)}
              className={`flex items-center gap-3 rounded-xl px-4 py-3.5 border-2 text-left transition-all cursor-pointer ${checked[i] ? "border-primary bg-primary/5" : "border-gray-200 bg-white hover:border-gray-300"}`}>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${checked[i] ? "border-primary bg-primary" : "border-gray-300"}`}>
                {checked[i] && <Check size={11} className="text-white" strokeWidth={3} />}
              </div>
              <span className={`text-sm ${checked[i] ? "text-primary font-medium" : "text-gray-700"}`}>{item}</span>
            </button>
          ))}
        </div>
        <motion.div className={`rounded-xl p-4 sm:p-5 text-center border transition-all ${allChecked ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}
          animate={{ scale: allChecked ? [1, 1.02, 1] : 1 }} transition={{ duration: 0.3 }}>
          {allChecked ? (
            <div className="flex items-center justify-center gap-2 text-green-700 font-semibold text-sm">
              <CheckCircle size={18} className="text-green-500" />
              All checks passed. You're ready to access patient information.
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{topic10?.callout}</p>
          )}
        </motion.div>
      </motion.div>
    </Card>
  );
}

function RealLifeScenario({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <motion.div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
          initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
            <LessonIcon name={content.incident?.icon ?? "AlertCircle"} size={18} className="text-primary" />
          </div>
          <h3 className="font-bold text-gray-900 mb-3 text-sm sm:text-base">{content.incident?.title}</h3>
          <p className="text-sm text-gray-500 leading-relaxed italic">{content.incident?.text}</p>
        </motion.div>
        <motion.div className="bg-white rounded-xl border border-red-100 p-5 sm:p-6"
          initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.2 }}>
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center mb-4">
            <LessonIcon name={content.assessment?.icon ?? "ShieldAlert"} size={18} className="text-red-400" />
          </div>
          <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">{content.assessment?.title}</h3>
          <p className="text-sm font-semibold text-red-500 mb-3">{content.assessment?.verdict}</p>
          <p className="text-sm text-gray-500 leading-relaxed">{content.assessment?.text}</p>
        </motion.div>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MODULE 4 LESSON TYPES
═══════════════════════════════════════════════════════════════ */

function BreachDefinition({ content }) {
  const { definition, examples } = content;
  const iconColorMap = { red: "bg-red-50 text-red-400", orange: "bg-orange-50 text-orange-400", purple: "bg-purple-50 text-purple-400", blue: "bg-blue-50 text-blue-400", yellow: "bg-yellow-50 text-yellow-400", teal: "bg-cyan-50 text-cyan-500" };
  return (
    <Card className="space-y-12 sm:space-y-14">
      <div>
        <SectionLabel>{definition?.sectionLabel}</SectionLabel>
        <Heading className="mb-3">{definition?.heading}</Heading>
        <p className="text-gray-500 leading-relaxed mb-6 text-sm sm:text-base">{definition?.body}</p>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 sm:p-5 mb-6">
          <div className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-4">{definition?.listLabel}</div>
          <div className="space-y-2.5">
            {(definition?.listItems ?? []).map((item, i) => (
              <motion.div key={i} className="flex items-center gap-3" initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.1 + i * 0.07 }}>
                <CheckCircle size={16} className="text-primary/50 shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-500 italic">{definition?.footer}</p>
      </div>
      <div>
        <SectionLabel>{examples?.sectionLabel}</SectionLabel>
        <Heading className="mb-6">{examples?.heading}</Heading>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {(examples?.items ?? []).map((item, i) => (
            <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.07 }}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${iconColorMap[item.iconColor] || "bg-gray-100 text-gray-400"}`}>
                <LessonIcon name={item.icon ?? "AlertCircle"} size={16} />
              </div>
              <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h4>
              <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
        {examples?.note && (
          <motion.div className="bg-cyan-50/60 border border-cyan-100 rounded-xl p-4 flex items-start gap-3"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.5 }}>
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0"><span className="text-white font-bold text-xs">!</span></div>
            <div>
              <div className="text-xs font-bold text-primary tracking-widest uppercase mb-1">{examples.note.label}</div>
              <p className="text-sm text-gray-600">{examples.note.text}</p>
            </div>
          </motion.div>
        )}
      </div>
    </Card>
  );
}

function BreachImmediateActions({ content }) {
  return (
    <Card>
      <SectionLabel>{content.sectionLabel}</SectionLabel>
      <Heading className="mb-3">{content.heading}</Heading>
      <p className="text-gray-500 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      <div className="space-y-4 mb-8">
        {(content.steps ?? []).map((step, i) => (
          <motion.div key={i} className="flex items-center gap-3 sm:gap-4"
            initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.09 }}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-cyan-100/70 flex items-center justify-center shrink-0">
              <span className="text-primary font-bold text-sm">{i + 1}</span>
            </div>
            <p className="font-semibold text-gray-800 text-sm sm:text-base">{step}</p>
          </motion.div>
        ))}
      </div>
      {content.callout && (
        <motion.div className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-5 flex items-start gap-3"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.6 }}>
          <LessonIcon name={content.callout.icon} size={16} className="text-primary shrink-0 mt-0.5" />
          <div>
            <div className="text-xs font-bold text-primary tracking-widest uppercase mb-1">{content.callout.label}</div>
            <p className="text-sm text-gray-500 leading-relaxed">{content.callout.text}</p>
          </div>
        </motion.div>
      )}
    </Card>
  );
}

function BreachSOP({ content }) {
  const { whySpeed, whatNotToDo } = content;
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-8 sm:mb-10">{content.heading}</Heading>
      <div className="mb-8 sm:mb-10">
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-lg font-bold text-primary">{whySpeed?.number}.</span>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">{whySpeed?.title}</h3>
        </div>
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
          <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100">
            <p className="text-sm text-gray-500">{whySpeed?.intro}</p>
          </div>
          {(whySpeed?.items ?? []).map((item, i) => (
            <motion.div key={i} className="px-4 sm:px-5 py-4 border-b border-gray-100 last:border-0"
              initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.1 + i * 0.08 }}>
              <p className="font-semibold text-gray-800 text-sm sm:text-base">{item}</p>
            </motion.div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-xl px-4 sm:px-5 py-4">
          <p className="text-sm text-gray-500 italic">{whySpeed?.footer}</p>
        </div>
      </div>
      <div>
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-lg font-bold text-red-400">{whatNotToDo?.number}.</span>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">{whatNotToDo?.title}</h3>
        </div>
        <div className="space-y-2.5 mb-4">
          {(whatNotToDo?.items ?? []).map((item, i) => (
            <motion.div key={i} className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl px-4 sm:px-5 py-3.5"
              initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.1 + i * 0.07 }}>
              <XCircle size={16} className="text-red-400 shrink-0" />
              <span className="font-semibold text-red-600 text-sm">{item}</span>
            </motion.div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-xl px-4 sm:px-5 py-4">
          <p className="text-sm text-gray-500 italic">{whatNotToDo?.footer}</p>
        </div>
      </div>
    </Card>
  );
}

function BreachIncidentRule({ content }) {
  const { incident, rule } = content;
  return (
    <div className="bg-white rounded-2xl space-y-5 sm:space-y-6">
      <motion.div className="bg-gray-300 rounded-2xl border border-gray-200 p-5 sm:p-8"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">{incident?.number}</span>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{incident?.heading}</h2>
        </div>
        <p className="text-gray-500 leading-relaxed mb-4 text-sm sm:text-base">{incident?.body1}</p>
        <p className="text-gray-500 leading-relaxed text-sm sm:text-base">{incident?.body2}</p>
      </motion.div>
      <motion.div className="bg-gray-900 rounded-2xl p-5 sm:p-8 relative overflow-hidden"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.25 }}>
        <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-10">
          <LessonIcon name={rule?.icon ?? "Scale"} size={64} className="text-white sm:hidden" />
          <LessonIcon name={rule?.icon ?? "Scale"} size={80} className="text-white hidden sm:block" />
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm">{rule?.number}</span>
          </div>
          <span className="text-xs font-bold text-primary tracking-widest uppercase">{rule?.label}</span>
        </div>
        <p className="text-white text-xl sm:text-2xl font-bold leading-snug mb-5">{rule?.quote}</p>
        <div className="flex items-center gap-3">
          <div className="w-8 h-px bg-primary" />
          <span className="text-xs font-bold text-primary/70 tracking-widest uppercase">{rule?.sublabel}</span>
        </div>
      </motion.div>
    </div>
  );
}

function BreachScenario({ content }) {
  const { situation, correctAction, rule } = content;
  return (
    <div className="bg-white rounded-2xl space-y-4 sm:space-y-5">
      {/* Section heading */}
      <div className="px-1">
        {content.subtitle && <SectionLabel>{content.subtitle}</SectionLabel>}
        <Heading>{content.heading ?? "Real-Life Scenario"}</Heading>
      </div>
      {[
        { data: situation, delay: 0.1, bg: "bg-gray-300 border border-gray-200", icon: situation?.icon ?? "Info", label: situation?.label, body: situation?.text },
        { data: correctAction, delay: 0.2, bg: "bg-gray-300 border border-gray-200", isSteps: true },
      ].map((block, bi) => (
        <motion.div key={bi} className={`${block.bg} rounded-xl p-5 sm:p-6`}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: block.delay }}>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <LessonIcon name={bi === 0 ? (situation?.icon ?? "Info") : (correctAction?.icon ?? "CheckCircle")} size={16} className="text-primary shrink-0" />
            <span className="text-xs font-bold text-primary tracking-widest uppercase">{bi === 0 ? situation?.label : correctAction?.label}</span>
          </div>
          {bi === 0 ? (
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{situation?.text}</p>
          ) : (
            <div className="space-y-3">
              {(correctAction?.steps ?? []).map((step, i) => (
                <p key={i} className={step.bold ? "font-semibold text-gray-800 text-sm sm:text-base" : "text-sm text-gray-400"}>{step.text}</p>
              ))}
            </div>
          )}
        </motion.div>
      ))}
      <motion.div className="bg-gray-900 rounded-xl p-5 sm:p-6 relative overflow-hidden"
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.35 }}>
        <div className="flex items-center gap-2 mb-3">
          <LessonIcon name={rule?.icon ?? "GitFork"} size={16} className="text-primary shrink-0" />
          <span className="text-xs font-bold text-primary tracking-widest uppercase">{rule?.label}</span>
        </div>
        <p className="text-white text-sm leading-relaxed mb-4">{rule?.text}</p>
        <div className="flex items-center gap-2 text-gray-500 text-xs">
          <LessonIcon name={rule?.sublabelIcon ?? "Info"} size={13} className="text-gray-600 shrink-0" />
          <span className="tracking-widest uppercase">{rule?.sublabel}</span>
        </div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MODULE 5 LESSON TYPES
═══════════════════════════════════════════════════════════════ */

function BaDefinition({ content }) {
  const { baSection, baaSection } = content;
  return (
    <Card className="space-y-10 sm:space-y-12">
      <div>
        <div className="flex items-baseline gap-3 mb-4">
          <span className="text-lg font-bold text-primary">{baSection?.number}.</span>
          <Heading>{baSection?.title}</Heading>
        </div>
        <p className="text-gray-500 leading-relaxed mb-6 text-sm sm:text-base">{baSection?.body}</p>
        {/* Single col on mobile, 2 on sm+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {(baSection?.examples ?? []).map((ex, i) => (
            <motion.div key={i} className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.08 }}>
              <div className="flex items-center gap-2 mb-2">
                <LessonIcon name={ex.icon ?? "Building"} size={16} className="text-primary" />
                <span className="text-xs font-bold text-gray-800 tracking-wider">{ex.title}</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{ex.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-gray-500 text-sm">{baSection?.footer}</p>
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}>
        <div className="flex items-baseline gap-3 mb-4">
          <span className="text-lg font-bold text-primary">{baaSection?.number}.</span>
          <Heading>{baaSection?.title}</Heading>
        </div>
        <div className="bg-cyan-50/60 rounded-xl p-5 sm:p-6 mb-6">
          <p className="text-gray-700 leading-relaxed mb-6 text-sm sm:text-base">
            A <span className="text-primary font-semibold">Business Associate Agreement (BAA)</span> is a legally required contract that must be in place before any PHI is shared.
          </p>
          {/* BAA diagram — stack on tiny screens */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-6">
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center mx-auto mb-2">
                <LessonIcon name="Building" size={18} className="text-gray-400" />
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-gray-500 tracking-wider">{baaSection?.diagram?.left}</span>
            </div>
            <div className="flex-1 h-px bg-gray-300 relative min-w-0">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-cyan-100 text-primary text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-1 rounded-full border border-primary/20 whitespace-nowrap">
                {baaSection?.diagram?.center}
              </span>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white border-2 border-primary flex items-center justify-center mx-auto mb-2">
                <LessonIcon name="ShieldCheck" size={18} className="text-primary" />
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-primary tracking-wider">{baaSection?.diagram?.right}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{baaSection?.body}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-100">
          <p className="text-sm text-gray-500 italic">{baaSection?.example}</p>
        </div>
      </motion.div>
    </Card>
  );
}

function SharedResponsibility({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-3">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">{content.body}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        {(content.columns ?? []).map((col, ci) => (
          <motion.div key={ci} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 + ci * 0.15 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ci === 0 ? "bg-gray-100" : "bg-primary/10"}`}>
                <LessonIcon name={ci === 0 ? "Building" : "ShieldCheck"} size={18} className={ci === 0 ? "text-gray-500" : "text-primary"} />
              </div>
              <h3 className="font-bold text-gray-900 text-sm sm:text-base">{col.title}</h3>
            </div>
            <div className="space-y-3">
              {(col.items ?? []).map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  {col.checkType === "check" ? <CheckCircle size={16} className="text-primary shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />}
                  <span className="text-sm text-gray-600">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div className="flex items-center justify-center gap-2 text-sm text-gray-500"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.5 }}>
        <Info size={16} className="text-primary shrink-0" />
        <span className="text-sm text-center">{content.footer}</span>
      </motion.div>
    </Card>
  );
}

function PlatformUsage({ content }) {
  return (
    <Card className="space-y-10 sm:space-y-12">
      <div>
        <SectionLabel>{content.sections?.[0]?.subtitle}</SectionLabel>
        <Heading className="mb-3">{content.sections?.[0]?.heading}</Heading>
        <p className="text-gray-500 mb-6 text-sm sm:text-base">{content.sections?.[0]?.intro}</p>
        <div className="space-y-3 mb-8">
          {(content.sections?.[0]?.items ?? []).map((item, i) => (
            <motion.div key={i} className="flex items-start gap-3 sm:gap-4 bg-white rounded-xl border border-gray-200 p-4 sm:p-5"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.1 + i * 0.08 }}>
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <LessonIcon name={item.icon} size={18} className="text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.5 }}>
        <SectionLabel>{content.sections?.[1]?.subtitle}</SectionLabel>
        <Heading className="mb-3">{content.sections?.[1]?.heading}</Heading>
        <p className="text-gray-500 mb-6 text-sm sm:text-base">{content.sections?.[1]?.intro}</p>
        <div className="space-y-4 mb-6">
          {(content.sections?.[1]?.checklist ?? []).map((item, i) => (
            <motion.div key={i} className="flex items-start gap-3 sm:gap-4"
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.6 + i * 0.1 }}>
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <LessonIcon name="FileSignature" size={18} className="text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="text-gray-500 text-sm mb-6 sm:mb-8">{content.sections?.[1]?.footer}</p>
        <motion.div className="bg-gray-900 rounded-xl p-6 sm:p-8 relative overflow-hidden"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.9 }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs font-bold text-primary tracking-widest">{content.sections?.[1]?.quote?.label}</span>
          </div>
          <p className="text-white text-base sm:text-lg leading-relaxed">{content.sections?.[1]?.quote?.text}</p>
        </motion.div>
      </motion.div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MODULE 6 LESSON TYPES
═══════════════════════════════════════════════════════════════ */

function StaffReminders({ content }) {
  return (
    <Card>
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-2">{content.heading}</Heading>
      <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">{content.intro}</p>
      <div className="space-y-8 sm:space-y-10">
        {(content.sections ?? []).map((section, si) => (
          <motion.div key={si} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: si * 0.2 }}>
            <div className="flex items-center gap-3 mb-4 sm:mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <LessonIcon name={si === 0 ? "Building" : "HeartPulse"} size={18} className="text-primary" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900">{section.number}. {section.title}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(section.columns ?? []).map((col, ci) => (
                <div key={ci} className={`rounded-xl p-4 sm:p-5 ${col.type === "always" ? "bg-green-50/50 border border-green-100" : "bg-red-50/50 border border-red-100"}`}>
                  <div className="flex items-center gap-2 mb-4">
                    {col.type === "always" ? <CheckCircle size={16} className="text-green-500" /> : <XCircle size={16} className="text-red-400" />}
                    <span className={`text-xs font-bold tracking-widest ${col.type === "always" ? "text-green-600" : "text-red-500"}`}>{col.title}</span>
                  </div>
                  <ul className="space-y-3">
                    {(col.items ?? []).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${col.type === "always" ? "bg-green-400" : "bg-red-400"}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

function BillingReminders({ content }) {
  return (
    <Card className="space-y-10 sm:space-y-12">
      <div>
        <SectionLabel>{content.sections?.[0]?.subtitle}</SectionLabel>
        <Heading className="mb-3">{content.sections?.[0]?.heading}</Heading>
        <p className="text-gray-500 mb-6 text-sm sm:text-base">{content.sections?.[0]?.intro}</p>
        <div className="space-y-2 mb-6">
          {(content.sections?.[0]?.items ?? []).map((item, i) => (
            <motion.p key={i} className="text-gray-700 text-sm sm:text-base"
              initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, delay: 0.1 + i * 0.05 }}>
              {item}
            </motion.p>
          ))}
        </div>
        {content.sections?.[0]?.tip && (
          <motion.div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4 }}>
            <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-medium">{content.sections[0].tip.text}</p>
          </motion.div>
        )}
      </div>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.5 }}>
        <SectionLabel>{content.sections?.[1]?.subtitle}</SectionLabel>
        <Heading className="mb-6">{content.sections?.[1]?.heading}</Heading>
        <div className="space-y-6">
          {(content.sections?.[1]?.groups ?? []).map((group, i) => (
            <motion.div key={i} className="flex items-start gap-3 sm:gap-4"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.6 + i * 0.1 }}>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <LessonIcon name={group.icon} size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">{group.title}</h3>
                <ul className="space-y-1">
                  {(group.items ?? []).map((item, j) => <li key={j} className="text-sm text-gray-500">{item}</li>)}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </Card>
  );
}

function FiveSecondRule({ content }) {
  return (
    <Card className="text-center">
      <SectionLabel>{content.subtitle}</SectionLabel>
      <Heading className="mb-6 sm:mb-8">{content.heading}</Heading>
      <motion.div className="bg-cyan-50/60 rounded-2xl p-6 sm:p-8 max-w-lg mx-auto"
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-5 sm:mb-6">
          <LessonIcon name={content.card?.icon ?? "Clock"} size={26} className="text-white" />
        </div>
        <p className="text-gray-700 mb-6 font-medium text-sm sm:text-base">{content.card?.intro}</p>
        <div className="space-y-4 mb-6 text-left">
          {(content.card?.steps ?? []).map((step, i) => (
            <motion.div key={i} className="flex items-center gap-3 sm:gap-4"
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.2 + i * 0.1 }}>
              <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border-2 border-primary text-primary font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="text-gray-700 font-medium text-sm sm:text-base">{step}</span>
            </motion.div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 text-amber-600 font-semibold text-sm pt-4 border-t border-cyan-200">
          <AlertTriangle size={16} />
          {content.card?.warning}
        </div>
      </motion.div>
    </Card>
  );
}

/* ─────────── Shared: Note Block ─────────── */
function NoteBlock({ note }) {
  return (
    <div className="border-l-4 border-primary bg-cyan-50/50 rounded-r-xl p-4 flex items-start gap-3">
      <Info size={16} className="text-primary shrink-0 mt-0.5" />
      <div>
        <div className="text-xs font-bold text-primary tracking-wider uppercase mb-1">{note?.title || "Important Note"}</div>
        <p className="text-sm text-gray-500 leading-relaxed">{note?.text}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   GENERIC / FALLBACK LESSON
   Renders all available fields from a section with content: null
═══════════════════════════════════════════════════════════════ */
function GenericLesson({ section }) {
  const listItems = section.list_items
    ? section.list_items.split("\n").filter(Boolean)
    : [];

  return (
    <Card>
      {section.subtitle && <SectionLabel>{section.subtitle}</SectionLabel>}
      {section.heading && <Heading className="mb-3">{section.heading}</Heading>}

      {section.definition && (
        <p className="text-base font-semibold text-gray-800 mb-4">{section.definition}</p>
      )}

      {section.body && (
        <p className="text-gray-500 leading-relaxed text-sm sm:text-base mb-6">{section.body}</p>
      )}

      {listItems.length > 0 && (
        <div className="space-y-2.5 mb-6">
          {listItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.1 + i * 0.06 }}
              className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100"
            >
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <LessonIcon name="CheckCircle" size={13} className="text-primary" />
              </div>
              <span className="text-sm text-gray-700 leading-snug">{item}</span>
            </motion.div>
          ))}
        </div>
      )}

      {section.quote && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-primary/5 border border-primary/10 rounded-xl px-5 py-4 mb-6"
        >
          <p className="text-sm text-primary font-medium italic leading-relaxed">
            &ldquo;{section.quote}&rdquo;
          </p>
        </motion.div>
      )}

      {section.callout && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="bg-amber-50 border border-amber-100 rounded-xl px-5 py-4"
        >
          <div className="flex items-start gap-3">
            <LessonIcon name="AlertTriangle" size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 leading-relaxed">{section.callout}</p>
          </div>
        </motion.div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN LESSON PAGE
═══════════════════════════════════════════════════════════════ */
export default function Lesson() {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const { completeLesson } = useProgress();
  const { data: modules, isLoading, isError } = useGetModulesQuery();

  // Always scroll to top when lesson changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [moduleId, lessonId]);

  if (isLoading) return <div className="p-8">Loading...</div>;
  if (isError) return <div className="p-8">Error loading modules</div>;

  const module = modules?.find((m) => m.order === Number(moduleId));
  if (!module) return <div className="p-8">Loading...</div>;

  const lessonIndex = module.sections.findIndex((s) => s.order === Number(lessonId));
  const lesson = module.sections[lessonIndex];
  if (!lesson) return <div className="p-8">Lesson not found</div>;

  const isLast = lessonIndex === module.sections.length - 1;
  const isFirst = lessonIndex === 0;
  const isFinalModule = !module.knowledge_checks?.length;

  const handleNext = () => {
    completeLesson(module.order, lesson.order, { moduleDbId: module.id, sectionDbId: lesson.id });
    if (isLast) {
      if (isFinalModule) {
        navigate(`/module/${module.order}?completed=true`);
      } else {
        navigate(`/module/${module.order}/quiz`);
      }
    } else {
      navigate(`/module/${module.order}/lesson/${module.sections[lessonIndex + 1].order}`);
    }
  };

  const handlePrev = () => {
    if (isFirst) {
      navigate(`/module/${module.order}`);
    } else {
      navigate(`/module/${module.order}/lesson/${module.sections[lessonIndex - 1].order}`);
    }
  };

  const renderContent = () => {
    const contentType = resolveContentType(lesson);
    const content = buildContent(lesson, contentType);
    switch (contentType) {
      case "centered-definition":    return <CenteredDefinition content={content} />;
      case "phi-definition":         return <PhiDefinition content={content} />;
      case "combined-lessons":       return <CombinedLessons content={content} />;
      case "real-life-examples":     return <RealLifeExamples content={content} />;
      case "responsibility":         return <ResponsibilityLesson content={content} />;
      case "numbered-cards":         return <NumberedCards content={content} />;
      case "highlight":              return <HighlightLesson content={content} />;
      case "rights":                 return <RightsLesson content={content} />;
      case "mistakes":               return <MistakesLesson content={content} />;
      case "verification-social-media": return <VerificationSocialMedia content={content} />;
      case "scenario-analysis":      return <ScenarioAnalysis content={content} />;
      case "three-safeguards":       return <ThreeSafeguards content={content} />;
      case "role-based-access":      return <RoleBasedAccess content={content} />;
      case "physical-safeguards":    return <PhysicalSafeguards content={content} />;
      case "technical-safeguards":   return <TechnicalSafeguards content={content} />;
      case "two-column-topics":      return <TwoColumnTopics content={content} />;
      case "remote-work-checklist":  return <RemoteWorkChecklist content={content} />;
      case "real-life-scenario":     return <RealLifeScenario content={content} />;
      case "breach-definition":      return <BreachDefinition content={content} />;
      case "breach-immediate-actions": return <BreachImmediateActions content={content} />;
      case "breach-sop":             return <BreachSOP content={content} />;
      case "breach-incident-rule":   return <BreachIncidentRule content={content} />;
      case "breach-scenario":        return <BreachScenario content={content} />;
      case "ba-definition":          return <BaDefinition content={content} />;
      case "shared-responsibility":  return <SharedResponsibility content={content} />;
      case "platform-usage":         return <PlatformUsage content={content} />;
      case "staff-reminders":        return <StaffReminders content={content} />;
      case "billing-reminders":      return <BillingReminders content={content} />;
      case "five-second-rule":       return <FiveSecondRule content={content} />;
      default:
        return <GenericLesson section={lesson} />;
    }
  };

  const nextLabel = isLast ? "Complete & Continue" : "Next Lesson";
  const nextIcon = isLast ? <Check size={16} /> : <ChevronRight size={16} />;
  const lessonBreadcrumb = getLessonBreadcrumb(module.order, module.sections, lesson);

  return (
    <div className="min-h-screen bg-surface">
      <Sidebar />
      <div className="ml-0 md:ml-16">
        <TopNav
          backTo="/dashboard"
          backLabel="BACK TO CURRICULUM"
          breadcrumbs={[`MODULE ${module.order}`, lessonBreadcrumb]}
          rightContent={
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-primary font-medium">
              FOCUS MODE ACTIVE <Settings size={14} />
            </span>
          }
        />

        <LessonLayout>
          <div className="flex flex-col gap-5 sm:gap-6">
            {renderContent()}

            {/* Navigation — stacks on mobile */}
            <div className="flex items-center justify-between gap-3 px-1 pb-4">
              <button onClick={handlePrev}
                className="flex items-center gap-1.5 text-gray-400 hover:text-primary transition-colors text-xs sm:text-sm font-medium cursor-pointer shrink-0">
                <ChevronLeft size={16} />
                <span className="hidden sm:inline">PREVIOUS LESSON</span>
                <span className="sm:hidden">BACK</span>
              </button>
              <button onClick={handleNext}
                className="flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-dark hover:to-primary text-white px-4 sm:px-6 py-2.5 rounded-full font-semibold text-xs sm:text-sm transition-all cursor-pointer shadow-lg shadow-primary/20">
                {nextLabel}
                {nextIcon}
              </button>
            </div>
          </div>
        </LessonLayout>
      </div>
    </div>
  );
}